/**
 * NEET Sorting Helper
 * Provides natural alphanumeric sorting (handling numbers like 1, 2, 10 in chapter names)
 * and alphabetical sorting for text topics without numbers.
 * 
 * Strictly isolated for NEET module.
 */

/**
 * Natural comparator for strings / numbers.
 * Handles embedded digits (Chapter-1 < Chapter-2 < Chapter-10),
 * case-insensitive, whitespace-trimmed, and null-safe.
 */
export const neetNaturalCompare = (a, b) => {
    if (a === b) return 0;
    if (a == null || a === '') return 1;
    if (b == null || b === '') return -1;

    return a.toString().trim().localeCompare(b.toString().trim(), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
};

/**
 * Sorts an array of objects by a specific property using natural comparison.
 * If DisplayOrder is defined and > 0, DisplayOrder takes precedence.
 */
export const sortNeetList = (array, key, direction = 'asc') => {
    if (!Array.isArray(array)) return [];

    return [...array].sort((a, b) => {
        const orderA = Number(a?.DisplayOrder) || 0;
        const orderB = Number(b?.DisplayOrder) || 0;
        if (orderA > 0 && orderB === 0) return -1;
        if (orderA === 0 && orderB > 0) return 1;
        if (orderA > 0 && orderB > 0 && orderA !== orderB) {
            return direction === 'desc' ? orderB - orderA : orderA - orderB;
        }

        const valA = a?.[key];
        const valB = b?.[key];
        const comp = neetNaturalCompare(valA, valB);
        return direction === 'desc' ? -comp : comp;
    });
};

/**
 * Multi-column hierarchical sort for NEET topics:
 * 1. Groups by Subject (Natural Ascending)
 * 2. Groups by Section (Natural Ascending, e.g. Class-11 before Class-12)
 * 3. Sorts Topics naturally according to topicDirection ('asc' or 'desc')
 */
export const sortNeetTopics = (array, topicDirection = 'asc') => {
    if (!Array.isArray(array)) return [];

    return [...array].sort((a, b) => {
        // 1. Subject Sort
        const subjectComp = neetNaturalCompare(a?.Subject, b?.Subject);
        if (subjectComp !== 0) return subjectComp;

        // 2. Section Sort
        const sectionComp = neetNaturalCompare(a?.Section, b?.Section);
        if (sectionComp !== 0) return sectionComp;

        // 3. Topic Natural Sort
        const topicComp = neetNaturalCompare(a?.Topic, b?.Topic);
        return topicDirection === 'desc' ? -topicComp : topicComp;
    });
};

